import React from 'react';
import { DataLayerGraphProps } from '@/types/shared/data-layers.types';
import { BaseCard } from '@/components/uiKit/Cards/BaseCard';
import { AlertCircle } from 'lucide-react';
import { getAbuseColor as getTableAbuseColor } from '../utils';

interface FleetMostFrequentDataLayerProps extends DataLayerGraphProps {
  onClose?: () => void;
}

const getAbuseColor = (abuseName: string | undefined) => {
  if (!abuseName) return 'bg-gray-500';

  // First try to match with the table colors for consistency
  // Remove any extra description after the dash for matching
  const baseName = abuseName.split(' - ')[0].trim();

  // Try exact match first
  let color = getTableAbuseColor(baseName);
  if (color !== 'bg-gray-500') return color;

  // Try partial matches for common patterns
  if (baseName.includes('Incorrect Driving')) return 'bg-rose-600';
  if (baseName.includes('Wrong Gear')) return 'bg-violet-600';
  if (baseName.includes('Hydraulic Brake')) return 'bg-sky-600';
  if (baseName.includes('Wheel Digging')) return 'bg-emerald-600';
  if (baseName.includes('Hot Engine')) return 'bg-yellow-600';
  if (baseName.includes('Truck') && baseName.includes('Loading')) return 'bg-amber-600';
  if (baseName.includes('Service Brake')) return 'bg-indigo-600';
  if (baseName.includes('Hot Lever') || baseName.includes('Lever Position')) return 'bg-teal-600';

  // Additional colors for other abuses
  const additionalColorMap: Record<string, string> = {
    'Excessive Idle Time': 'bg-cyan-600',
    'Harsh Acceleration': 'bg-purple-600',
    'Speeding Violation': 'bg-red-600',
    'Improper Gear Selection': 'bg-orange-600',
    'Engine Over-revving': 'bg-lime-600',
    'Sudden Direction Change': 'bg-green-600',
    'Payload Overload': 'bg-blue-600',
    'Cold Engine Operation': 'bg-pink-600',
    'Improper Parking': 'bg-fuchsia-600',
    'Hydraulic System Abuse': 'bg-slate-600',
    'Transmission Abuse': 'bg-zinc-600',
    'Brake System Misuse': 'bg-stone-600',
  };

  return additionalColorMap[baseName] || 'bg-gray-500';
};

export const FleetMostFrequentDataLayer: React.FC<FleetMostFrequentDataLayerProps> = ({
  graph,
  data,
  legendItems,
  onLegendToggle,
  loading = false,
  className = '',
  onClose,
}) => {
  const abuses = data?.abuses || [];
  const cardClasses = `flex flex-col ${className}`;

  if (loading) {
    return (
      <BaseCard
        variant="wrapper"
        title={
          <div className="flex items-center gap-3">
            <AlertCircle className="w-4 h-4" />
            <span className="text-base">{graph.title || 'Event Type Breakdown'}</span>
          </div>
        }
        classNames={{
          card: cardClasses,
          title: 'flex-shrink-0',
          content: 'flex-1 min-h-0 flex items-center justify-center',
        }}
      >
        <div className="text-gray-500">Loading abuse data...</div>
      </BaseCard>
    );
  }

  if (!abuses || abuses.length === 0) {
    return (
      <BaseCard
        variant="wrapper"
        title={
          <div className="flex items-center gap-3">
            <AlertCircle className="w-4 h-4" />
            <span className="text-base">{graph.title || 'Event Type Breakdown'}</span>
          </div>
        }
        classNames={{
          card: cardClasses,
          title: 'flex-shrink-0',
          content: 'flex-1 min-h-0 flex items-center justify-center',
        }}
      >
        <div className="text-gray-500 text-sm">No data available</div>
      </BaseCard>
    );
  }

  return (
    <BaseCard
      variant="wrapper"
      title={
        <div className="flex items-center gap-3">
          <AlertCircle className="w-4 h-4" />
          <span className="text-base">{graph.title || 'Event Type Breakdown'}</span>
        </div>
      }
      classNames={{
        card: cardClasses,
        title: 'flex-shrink-0',
        content: 'flex-1 min-h-0 overflow-hidden rounded-lg py-3',
      }}
    >
      <div className="space-y-2.5 h-full overflow-y-auto pr-2 scrollbar-thin ">
        {abuses.map((abuse: any, i: number) => {
          const bgColor = getAbuseColor(abuse.abuseName);
          return (
            <React.Fragment key={abuse.id}>
              <div className="flex items-center justify-between gap-2 pl-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm text-gray-500 font-semibold w-5 flex-shrink-0">{i + 1}</span>
                  <div className={`w-1 h-2 flex-shrink-0 ${bgColor}`}></div>
                  <span className="text-sm text-gray-700 truncate" title={abuse.abuseName}>
                    {abuse.abuseName}
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-900 flex-shrink-0">{abuse.percentage}%</span>
              </div>
              {i < abuses.length - 1 && <div className="border-b border-neutral-50" />}
            </React.Fragment>
          );
        })}
      </div>
    </BaseCard>
  );
};

// Export the configuration for this data layer
export const fleetMostFrequentConfig = {
  id: 'fleet-most-frequent',
  title: 'Event Type Breakdown',
  type: 'custom' as const,
  customComponent: FleetMostFrequentDataLayer,
  legend: [],
  features: ['ranking', 'percentage'],
};
